// client/src/hooks/useTemplateCertificado.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api'; 
import useAlert from './useAlert';       
// REMOVIDO: import { TEMPLATES_QUERY_KEY } from './useTemplates'; 

// ============================================================================
// HOOKS ESPECÍFICOS PARA TEMPLATE DE CERTIFICADO
// ============================================================================

const BASE_ROUTE = 'templates/certificado';
// REMOVIDO: const TEMPLATE_TYPE = 'certificado';

// CHAVE ESPECÍFICA PARA CERTIFICADOS
const CERTIFICADO_TEMPLATES_QUERY_KEY = 'templatesCertificado';

// Hook para buscar UMA LISTA de templates de certificado
export const useTemplatesCertificado = (params = {}) => {
    const { setAlert } = useAlert();

    return useQuery({
        queryKey: [CERTIFICADO_TEMPLATES_QUERY_KEY, params], // Usando a chave específica
        queryFn: async () => {
            console.log("FRONTEND LOG: useTemplatesCertificado (lista) - Iniciando busca. Parâmetros:", params);
            try {
                const { data } = await apiClient.get(`/${BASE_ROUTE}`, { params });
                console.log("FRONTEND LOG: useTemplatesCertificado (lista) - Resposta SUCESSO da API:", data);
                return (data.data || data || []).filter(Boolean);
            } catch (error) {
                console.error("FRONTEND LOG: useTemplatesCertificado (lista) - Erro na requisição API:", error);
                const errorMsg = error.response?.data?.error || 'Erro ao carregar templates de certificado.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        staleTime: 1000 * 60 * 30, 
    });
};

// Variáveis reconhecidas por populateCertificateTemplate
// (server/utils/certificateUtils.js).
export const useCertificadoPlaceholders = () => {
    return useQuery({
        queryKey: [CERTIFICADO_TEMPLATES_QUERY_KEY, 'placeholders'],
        queryFn: async () => {
            const { data } = await apiClient.get(`/${BASE_ROUTE}/placeholders`);
            return data.data || [];
        },
        staleTime: 1000 * 60 * 30,
    });
};

// Hook para buscar UM template de certificado por ID
export const useTemplateCertificadoById = (id, options = {}) => {
    const { setAlert } = useAlert();

    return useQuery({
        queryKey: [CERTIFICADO_TEMPLATES_QUERY_KEY, id], // Usando a chave específica
        queryFn: async () => {
            console.log("FRONTEND LOG: useTemplateCertificadoById - Iniciando busca. ID:", id);
            if (!id) {
                console.warn("FRONTEND LOG: useTemplateCertificadoById - 'id' não fornecido. Retornando null.");
                return null;
            }
            try {
                const { data } = await apiClient.get(`/${BASE_ROUTE}/${id}`);
                console.log("FRONTEND LOG: useTemplateCertificadoById - Resposta SUCESSO da API:", data);
                return data.data || data;
            } catch (error) {
                console.error("FRONTEND LOG: useTemplateCertificadoById - Erro na requisição API:", error);
                const errorMsg = error.response?.data?.error || 'Erro ao carregar detalhes do template de certificado.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        enabled: !!id, 
        staleTime: 1000 * 60 * 30,
        ...options
    });
};

// Gera um HTML+CSS de certificado por IA a partir de uma descrição de estilo
// — não persiste nada, só devolve o HTML pro editor visual (mesmo espírito
// do gerador de banner por IA já existente em BannersHomeFormPage.js).
export const useGerarCertificadoIA = () => {
    const { setAlert } = useAlert();
    return useMutation({
        mutationFn: async (descricaoEstilo) => {
            const { data } = await apiClient.post(`/${BASE_ROUTE}/gerar-ia`, { descricaoEstilo });
            return data.data;
        },
        onError: (err) => {
            setAlert(err.response?.data?.error || 'Erro ao gerar certificado com IA.', 'danger');
        },
    });
};

// Hook para operações de mutação (Adicionar, Atualizar, Deletar, Importar, Exportar) em templates de certificado
export const useMutateTemplateCertificado = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateCertificadoTemplatesCache = (id = null) => {
        if (id) queryClient.invalidateQueries({ queryKey: [CERTIFICADO_TEMPLATES_QUERY_KEY, id] });
        queryClient.invalidateQueries({ queryKey: [CERTIFICADO_TEMPLATES_QUERY_KEY] });
    };

    const addTemplateCertificado = useMutation({
        mutationFn: async (templateData) => {
            console.log("FRONTEND LOG: addTemplateCertificado - Tentando adicionar template. Dados:", templateData);
            const response = await apiClient.post(`/${BASE_ROUTE}`, templateData);
            return response.data;
        },
        onSuccess: (data) => {
            invalidateCertificadoTemplatesCache();
            setAlert('Template de certificado criado com sucesso!', 'success');
            console.log("FRONTEND LOG: addTemplateCertificado - Sucesso. Resposta:", data);
            return data;
        },
        onError: (err) => {
            const errorMsg = err.message || err.response?.data?.error || 'Erro ao criar template de certificado';
            setAlert(errorMsg, 'danger');
            console.error("FRONTEND LOG: addTemplateCertificado - Erro:", err);
            throw err;
        },
    });

    const updateTemplateCertificado = useMutation({
        mutationFn: async ({ id, templateData }) => {
            console.log("FRONTEND LOG: updateTemplateCertificado - Tentando atualizar template. ID:", id, "Dados:", templateData);
            const response = await apiClient.put(`/${BASE_ROUTE}/${id}`, templateData);
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateCertificadoTemplatesCache(variables.id);
            queryClient.setQueryData([CERTIFICADO_TEMPLATES_QUERY_KEY, variables.id], data.data || data); // Usando a chave específica
            setAlert('Template de certificado atualizado com sucesso!', 'success');
            console.log("FRONTEND LOG: updateTemplateCertificado - Sucesso. Resposta:", data);
            return data;
        },
        onError: (err) => {
            const errorMsg = err.message || err.response?.data?.error || 'Erro ao atualizar template de certificado';
            setAlert(errorMsg, 'danger');
            console.error("FRONTEND LOG: updateTemplateCertificado - Erro:", err);
            throw err;
        },
    });

    const deleteTemplateCertificado = useMutation({
        mutationFn: async (id) => {
            console.log("FRONTEND LOG: deleteTemplateCertificado - Tentando deletar template. ID:", id);
            const response = await apiClient.delete(`/${BASE_ROUTE}/${id}`);
            return response.data;
        },
        onSuccess: (data, id) => {
            invalidateCertificadoTemplatesCache();
            queryClient.removeQueries({ queryKey: [CERTIFICADO_TEMPLATES_QUERY_KEY, id] }); // Usando a chave específica
            setAlert('Template de certificado removido com sucesso!', 'success');
            console.log("FRONTEND LOG: deleteTemplateCertificado - Sucesso. Resposta:", data);
        },
        onError: (err) => {
            const errorMsg = err.response?.data?.error || 'Erro ao deletar template de certificado';
            setAlert(errorMsg, 'danger');
            console.error("FRONTEND LOG: deleteTemplateCertificado - Erro:", err);
            throw err;
        },
    });

    const importTemplateCertificado = useMutation({
        mutationFn: async (file) => {
            console.log("FRONTEND LOG: importTemplateCertificado - Tentando importar template. Arquivo:", file?.name);
            const formData = new FormData();
            formData.append("file", file);
            const response = await apiClient.post(`/${BASE_ROUTE}/import`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response.data;
        },
        onSuccess: (data) => {
            invalidateCertificadoTemplatesCache();
            setAlert('Template de certificado importado com sucesso!', 'success');
            console.log("FRONTEND LOG: importTemplateCertificado - Sucesso. Resposta:", data);
        },
        onError: (err) => {
            const errorMsg = err.response?.data?.error || 'Erro ao importar template de certificado';
            setAlert(errorMsg, 'danger');
            console.error("FRONTEND LOG: importTemplateCertificado - Erro:", err);
            throw err;
        },
    });

    const exportTemplateCertificado = async (id) => {
        try {
            console.log("FRONTEND LOG: exportTemplateCertificado - Tentando exportar template. ID:", id);
            const response = await apiClient.get(`/${BASE_ROUTE}/${id}/export`, { responseType: "blob" });
            
            const blob = new Blob([response.data], { type: "application/json" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `certificado-template-${id}.json`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            console.log("FRONTEND LOG: exportTemplateCertificado - Template exportado com sucesso.");
            setAlert('Template exportado com sucesso!', 'success');
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Erro ao exportar template de certificado';
            setAlert(errorMsg, 'danger');
            console.error("FRONTEND LOG: exportTemplateCertificado - Erro:", err);
            throw err;
        }
    };

    return { 
        addTemplateCertificado, 
        updateTemplateCertificado, 
        deleteTemplateCertificado,
        importTemplateCertificado,
        exportTemplateCertificado,
        isPendingCertificado: addTemplateCertificado.isPending || updateTemplateCertificado.isPending || deleteTemplateCertificado.isPending || importTemplateCertificado.isPending
    };
};